import { RideBar } from "../components/RideBar";
import { reqToJoinApi, rideRequestsApi, searchRidesApi } from "services/auth/rideApis";
import { addRecurringRequestApi } from "services/auth/recurringApis";
import { useSearchParams, useNavigate } from "react-router-dom";
import { addInterestApi } from "services/auth/interestApis";
import { FaBell } from "react-icons/fa6";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export const RideDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rides, setRides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingRequests, setExistingRequests] = useState(new Set());
  const [requestInProgress, setRequestInProgress] = useState(new Set());

  const from = searchParams?.get("from");
  const to = searchParams?.get("to");
  const date = searchParams?.get("date");

  // Fetch rides based on search params
  useEffect(() => {
    const fetchRides = async () => {
      try {
        setLoading(true);
        const data = await searchRidesApi(from || "", to || "", date || "");
        setRides(data);
      } catch (error) {
        console.error("Failed to fetch rides:", error);
        toast.error("Failed to load rides");
        setRides([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRides();
  }, [from, to, date]);

  // Fetch existing requests
  useEffect(() => {
    const fetchExistingRequests = async () => {
      try {
        const requests = await rideRequestsApi();
        const requestedRideIds = new Set(requests.map((req) => req.rideId));
        setExistingRequests(requestedRideIds);
      } catch (error) {
        console.error("Failed to fetch existing requests:", error);
      }
    };
    fetchExistingRequests();
  }, []);

  const handleJoinReq = async (id) => {
    try {
      setRequestInProgress((prev) => new Set([...prev, id]));

      const response = await reqToJoinApi(id);
      if (response) {
        // Update existingRequests to reflect the new request
        setExistingRequests((prev) => new Set([...prev, id]));
      }
    } catch (error) {
      console.error("Failed to send join request:", error);
      toast.error("Failed to send request");
    } finally {
      setRequestInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleRecurringJoinReq = async (rideId, recurrenceGroupId) => {
    try {
      setRequestInProgress((prev) => new Set([...prev, "grp_"+recurrenceGroupId]));
      await addRecurringRequestApi(recurrenceGroupId, rideId);
    } finally {
      setRequestInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete("grp_"+recurrenceGroupId);
        return newSet;
      });
    }
  };

  const registerInterest = async () => {
    await addInterestApi(from, to, date ? date : null);
  };

  if (loading) {
    return (
      <div className="max-sm:backdrop-blur-sm overflow-x-hidden hideScrollBars flex-1 flex justify-center items-center py-4 px-2 md:px-6 backdrop-blur z-10 w-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-zinc-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-sm:backdrop-blur-sm overflow-x-hidden hideScrollBars flex-1 flex justify-center py-4 px-2 md:px-6 backdrop-blur z-10 w-full">
      {rides?.length > 0 ? (
        <div className="space-y-4 h-fit flex flex-col items-center w-full">
          {rides?.map((ride, i) => {
            const isRequested = existingRequests.has(ride.id);
            const isInProgress = requestInProgress.has(ride.id);

            return (
              <div
                className="relative max-md:space-y-1 max-md:flex-col w-full max-w-[90vw] md:max-w-2xl"
                key={ride.id || i}
              >
                <div className="flex justify-center w-full">
                  <div className="w-full">
                    <RideBar ride={ride} />
                  </div>
                </div>
                <div className="absolute right-[1rem] bottom-[2rem] flex max-md:static max-md:w-full max-md:flex-col mx-auto gap-2">
                  <Button
                    className="p-2 rounded-xl px-6 w-full md:w-fit h-fit bg-amber-500 disabled:opacity-50"
                    onClick={() => handleJoinReq(ride.id)}
                    disabled={isRequested || isInProgress}
                  >
                    {isRequested
                      ? "Request Sent"
                      : isInProgress
                        ? "Sending..."
                        : "Request to join"}
                  </Button>
                  
                  {ride.recurrenceGroupId && (
                      <Button
                        className="p-2 rounded-xl px-6 w-full md:w-fit h-fit bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                        onClick={() => handleRecurringJoinReq(ride.id, ride.recurrenceGroupId)}
                        disabled={requestInProgress.has("grp_"+ride.recurrenceGroupId)}
                      >
                        {requestInProgress.has("grp_"+ride.recurrenceGroupId) ? "Sending..." : "Request Recurring Route"}
                      </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="items-center justify-center flex flex-col h-full">
          <div className="items-center p-8 rounded-lg text-center justify-center flex flex-col bg-white/80">
            <span className="text-3xl font-semibold">
              No matching rides found for your search criteria.
            </span>
            {from && to && from.length > 0 && to.length > 0 && (
              <>
                <span className="text-xl text-zinc-500 mt-4 mb-2">
                  Receive an email notification when a matching ride becomes
                  available?
                </span>
                <button
                  onClick={registerInterest}
                  className="p-2 rounded-lg bg-amber-500 text-white drop-shadow-md font-semibold inline-flex justify-center items-center gap-1"
                >
                  <FaBell /> Notify Me
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};